import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSearch, FaUsers } from "react-icons/fa";
import { getFPOBroadcast, getFPOBroadcastRecipients } from "../../services/fpoBroadcast";
import PageHeader from "../../components/header/PageHeader";
import SearchableSelect from "../../components/inputFields/SearchableSelect";
import { ChannelBadge, AudienceBadge, DispatchStatusBadge, RecipientStatusBadge, Pagination } from "./shared";

const PAGE_SIZE = 15;

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const InfoCell = ({ label, children }) => (
    <div>
        <p className="text-[11px] text-gray-400 font-medium">{label}</p>
        <div className="text-sm font-semibold text-gray-800 mt-0.5">{children}</div>
    </div>
);

const BroadcastDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [broadcast, setBroadcast] = useState(null);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);

    const [recipients, setRecipients] = useState([]);
    const [recLoading, setRecLoading] = useState(true);
    const [search, setSearch]                   = useState("");
    const [debouncedSearch, setDebouncedSearch]  = useState("");
    const [statusFilter, setStatusFilter]        = useState("all");
    const [page, setPage]   = useState(1);
    const [count, setCount] = useState(0);
    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

    const loadBroadcast = async () => {
        setLoading(true);
        setError(null);
        try {
            const res  = await getFPOBroadcast(id);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                setBroadcast(data.data);
            } else {
                setError(data?.message || "Failed to load broadcast.");
            }
        } catch {
            setError("Unable to connect. Please check the server.");
        } finally {
            setLoading(false);
        }
    };

    const loadRecipients = async () => {
        setRecLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            if (statusFilter !== "all") params.status = statusFilter;

            const res  = await getFPOBroadcastRecipients(id, params);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                setRecipients(data.results || []);
                setCount(data.count ?? 0);
            }
        } catch {
            // recipients failing to load shouldn't block the header view
        } finally {
            setRecLoading(false);
        }
    };

    useEffect(() => { loadBroadcast(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        loadRecipients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, page, debouncedSearch, statusFilter]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-500 text-sm">Loading broadcast…</p>
            </div>
        );
    }

    if (error || !broadcast) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <p className="text-red-500 font-medium">{error || "Broadcast not found."}</p>
                <button onClick={() => navigate("/notifications")} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">
                    Back to Broadcasts
                </button>
            </div>
        );
    }

    return (
        <div className="w-full pb-12">
            <PageHeader title={broadcast.number} />

            <button
                onClick={() => navigate("/notifications")}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors mb-4"
            >
                <FaArrowLeft size={11} /> Back to Broadcasts
            </button>

            {/* ── Header info ── */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5"
            >
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{broadcast.title || broadcast.template?.name || "Untitled broadcast"}</h2>
                        <div className="flex items-center gap-2 mt-1.5">
                            <ChannelBadge channel={broadcast.channel} />
                            <AudienceBadge type={broadcast.audience_type} />
                            <DispatchStatusBadge sentAt={broadcast.sent_at} />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-gray-100 pt-4">
                    <InfoCell label="Total Recipients">{broadcast.total_recipients}</InfoCell>
                    <InfoCell label="Sent">{broadcast.sent_count}</InfoCell>
                    <InfoCell label="Created By">{broadcast.created_by || "—"}</InfoCell>
                    <InfoCell label="Created">{fmtDateTime(broadcast.created_at)}</InfoCell>
                </div>

                {broadcast.message_body && (
                    <div className="border-t border-gray-100 mt-4 pt-4">
                        <p className="text-[11px] text-gray-400 font-medium mb-1">Message</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-xl p-3.5">{broadcast.message_body}</p>
                    </div>
                )}
            </motion.div>

            {/* ── Recipients ── */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
                    <h3 className="font-bold text-gray-800 text-sm">Recipients</h3>
                    <div className="flex items-center gap-2.5 flex-wrap">
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search name or mobile…"
                                className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-gray-50 w-56"
                            />
                        </div>
                        <div className="w-40">
                            <SearchableSelect
                                value={statusFilter}
                                onChange={(v) => { setStatusFilter(v); setPage(1); }}
                                options={[
                                    { value: "all", label: "All Status" },
                                    { value: "pending", label: "Pending" },
                                    { value: "sent", label: "Sent" },
                                    { value: "failed", label: "Failed" },
                                ]}
                                className="pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50"
                            />
                        </div>
                    </div>
                </div>

                {recLoading ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-2">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : recipients.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-2">
                        <FaUsers className="text-gray-200 text-4xl" />
                        <p className="text-gray-400 text-sm">No recipients match your filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-left text-[11px] text-gray-500 uppercase tracking-wide">
                                    <th className="px-5 py-3 font-semibold">Name</th>
                                    <th className="px-5 py-3 font-semibold">Mobile</th>
                                    <th className="px-5 py-3 font-semibold">Status</th>
                                    <th className="px-5 py-3 font-semibold">Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {recipients.map((r) => (
                                        <motion.tr
                                            key={r.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60 transition-colors"
                                        >
                                            <td className="px-5 py-3.5 font-medium text-gray-800">{r.name || "—"}</td>
                                            <td className="px-5 py-3.5 text-gray-600">{r.mobile}</td>
                                            <td className="px-5 py-3.5"><RecipientStatusBadge status={r.status} /></td>
                                            <td className="px-5 py-3.5 text-red-400 text-xs">{r.error_message || "—"}</td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="px-5 pb-4">
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onPrev={() => setPage((p) => Math.max(1, p - 1))}
                        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                    />
                </div>
            </div>
        </div>
    );
};

export default BroadcastDetail;
